import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, ChatsCircle, Scales, Receipt } from "@phosphor-icons/react";
import { useNotifications } from "../context/NotificationsContext";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function iconFor(link) {
  if (!link) return Bell;
  if (link.includes("questions")) return ChatsCircle;
  if (link.includes("recurring")) return Scales;
  if (link.includes("payment")) return Receipt;
  return Bell;
}

export default function NotificationsBell({ className = "" }) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (open && panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const go = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) nav(n.link);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 hover:bg-[#F5F5F5] transition-all"
        title="Notifications"
        data-testid="notifications-trigger"
        aria-label="Notifications"
      >
        <Bell size={20} weight="duotone" />
        {unreadCount > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#DC2626] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-[360px] max-w-[90vw] bg-white border border-[#E5E5E5] shadow-xl z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
            <div className="font-heading font-semibold text-sm">Notifications</div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11px] label-tag text-[#002FA7] hover:underline"
                data-testid="notifications-mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center">
                <CheckCircle size={28} weight="duotone" className="mx-auto text-[#10B981] mb-2" />
                <div className="text-sm text-[#525252]">You&apos;re all caught up.</div>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = iconFor(n.link);
                return (
                  <button
                    key={n.id}
                    onClick={() => go(n)}
                    data-testid={`notification-${n.id}`}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-[#E5E5E5] last:border-b-0 transition-all hover:bg-[#FAFAFA] ${
                      n.read ? "" : "bg-[#002FA7]/[0.04]"
                    }`}
                  >
                    <div className={`mt-0.5 w-8 h-8 flex-shrink-0 flex items-center justify-center ${n.read ? "bg-[#F5F5F5] text-[#525252]" : "bg-[#002FA7] text-white"}`}>
                      <Icon size={16} weight="duotone" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm ${n.read ? "text-[#525252]" : "font-medium"}`}>{n.title}</div>
                      {n.body && <div className="text-[12px] text-[#525252] mt-0.5 line-clamp-2">{n.body}</div>}
                      <div className="label-tag text-[10px] mt-1 text-[#a3a3a3]">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#DC2626] mt-2 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
